// 작업지시
import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { SkeletonRows } from '../components/Skeleton'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import { useGlobalReveal } from '../hooks/useReveal'
import { useAuth, hasPerm } from '../lib/auth'
import { useT } from '../lib/i18n'

interface WO {
  work_order_id: string
  production_order_id: string
  line_id: string
  equipment_id: string
  worker_id: string
  start_date: string
  end_date: string
  status: string
  item_code?: string
  quantity?: number
}

export function WorkOrdersPage() {
  useGlobalReveal()
  const toast = useToast()
  const { t } = useT()
  const { user } = useAuth()
  const canWrite = hasPerm(user, 'work_order', 'write')

  const [list, setList] = useState<WO[] | null>(null)
  const [form, setForm] = useState({
    production_order_id: '',
    line_id: 'L01',
    equipment_id: 'EQ-CNC-001',
    worker_id: 'OP-01',
    start_date: '2026-05-25',
    end_date: '2026-05-28',
  })
  const [busy, setBusy] = useState(false)

  const reload = async () => {
    try {
      const data = await api.get<WO[]>('/work-orders')
      setList(data)
    } catch (e: any) {
      toast.push('error', e?.message ?? t('toast.fetchFailed'))
    }
  }
  useEffect(() => { reload() }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      await api.post('/work-orders', form)
      toast.push('success', t('wo.created'))
      reload()
    } catch (er: any) {
      toast.push('error', er?.message ?? t('toast.actionFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main id="main" className="container stack" style={{ paddingBlock: '2rem' }}>
      <h1 className="page-title reveal">
        <FontAwesomeIcon icon="industry" /> {t('wo.title')}
      </h1>

      {canWrite && (
        <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="new-wo">
          <h2 id="new-wo" style={{ fontSize: 'var(--fs-lg)' }}>{t('wo.new')}</h2>
          <form
            onSubmit={submit}
            style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))' }}
          >
            {(
              [
                ['production_order_id', t('wo.field.poId'),       'text', 'PO-2026-0001'],
                ['line_id',             t('wo.field.line'),       'text', 'L01'],
                ['equipment_id',        t('wo.field.equipment'),  'text', 'EQ-CNC-001'],
                ['worker_id',           t('wo.field.worker'),     'text', 'OP-01'],
                ['start_date',          t('wo.field.startDate'),  'date', ''],
                ['end_date',            t('wo.field.endDate'),    'date', ''],
              ] as const
            ).map(([k, label, type, placeholder]) => (
              <div className="field" key={k}>
                <label htmlFor={k}>{label}</label>
                <input
                  id={k}
                  type={type}
                  required
                  placeholder={placeholder}
                  value={(form as any)[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                />
              </div>
            ))}
            <div style={{ alignSelf: 'end' }}>
              <Button type="submit" variant="accent" disabled={busy} aria-busy={busy}>
                <FontAwesomeIcon icon={busy ? 'circle-notch' : 'circle-plus'} spin={busy} />
                <span>{t('common.issue')}</span>
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card glass tilt={false} spotlight={false} className="reveal">
        {!list ? (
          <SkeletonRows rows={6} />
        ) : list.length === 0 ? (
          <div className="empty-state" role="status">
            <FontAwesomeIcon icon="industry" className="icon-lg" />
            <p className="muted" style={{ margin: 0 }}>{t('wo.empty')}</p>
            {canWrite && (
              <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
                {t('wo.emptyHint')}
              </p>
            )}
          </div>
        ) : (
          <div className="table-wrap" style={{ '--table-min-w': '900px' } as React.CSSProperties}>
            <table className="table" aria-label={t('wo.aria.table')}>
              <thead>
                <tr>
                  <th>{t('wo.col.id')}</th>
                  <th>{t('wo.col.poId')}</th>
                  <th>{t('wo.col.item')}</th>
                  <th>{t('wo.col.line')}</th>
                  <th>{t('wo.col.equipment')}</th>
                  <th>{t('wo.col.worker')}</th>
                  <th>{t('wo.col.period')}</th>
                  <th>{t('wo.col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {list.map((w) => (
                  <tr key={w.work_order_id}>
                    <td className="mono">{w.work_order_id}</td>
                    <td className="mono">{w.production_order_id}</td>
                    <td>{w.item_code}</td>
                    <td>{w.line_id}</td>
                    <td>{w.equipment_id}</td>
                    <td>{w.worker_id}</td>
                    <td>{w.start_date} → {w.end_date}</td>
                    <td>
                      <span className={`badge ${w.status === 'running' ? 'info' : w.status === 'done' ? 'success' : ''}`}>
                        {w.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </main>
  )
}
